import { Spinner } from "@/components/spinner/Spinner";
import { FontSizeProvider } from "@/contexts/font-size/FontSizeProvider";
import { AuthProvider } from "@/features/auth/auth-context/AuthContext";
import { ClientConfigProvider } from "@/features/client-config/contexts/client-config/ClientConfigContext";
import { TimeoutConfigProvider } from "@/features/client-config/contexts/timeout-config/TimeoutConfigContext";
import { UnitProvider } from "@/features/client-config/contexts/unit/UnitContext";
import { SentryDeviceContext } from "@/features/monitoring/sentry-device-context/sentry-device-context";
import { SessionShotsProvider } from "@/features/shot-tracking/contexts/session-shots/SessionShotsContext";
import { globalWebSocketManager } from "@/features/shot-tracking/services/global-websocket-manager";
import { useMountEffect } from "@/hooks/use-mount-effect";
import { AppRouter } from "@/router/AppRouter";
import { ApiProvider } from "@/services/api/api-provider/ApiProvider";
import { claimQueueManager } from "@/services/claim-queue/claim-queue-manager";
import { claimSyncEngine } from "@/services/claim-queue/claim-sync-engine";
import { i18n, initI18n, isI18nReady } from "@/services/i18n/i18n";
import { storageManager } from "@/services/storage/storage/storage";
import { useState } from "react";

import { runInitWithRetry } from "./App.utils";
import { NotificationToaster } from "./components/toaster/notification-toaster/NotificationToaster";
import { SnackbarToaster } from "./components/toaster/snackbar-toaster/SnackbarToaster";
import { Toaster } from "./components/toaster/toaster/Toaster";

import styles from "@/App.module.css";

export const App = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initFailed, setInitFailed] = useState(false);

  useMountEffect(() => {
    const initializePWA = async () => {
      try {
        await runInitWithRetry({
          init: async () => {
            await Promise.all([storageManager.initialize(), initI18n()]);
            claimQueueManager.setDatabase(storageManager.getDatabase()!);
            claimSyncEngine.start();
          },
        });
        setIsInitialized(true);
      } catch (error) {
        console.error("Failed to initialize PWA:", error);
        setInitFailed(true);
      }
    };

    initializePWA();

    return () => {
      globalWebSocketManager.destroy();
      claimSyncEngine.stop();
    };
  });

  if (initFailed) {
    // Falls back to plain English when initI18n() itself is what failed,
    // since translations can't be trusted to have loaded in that case.
    const t = isI18nReady()
      ? i18n.t.bind(i18n)
      : (_key: string, fallback: string) => fallback;
    return (
      <div className={styles.app}>
        <div className={styles.loading}>
          <p>{t("app.init-failed-title", "Unable to start")}</p>
          <p>
            {t(
              "app.init-failed-message",
              "The app couldn't start after several attempts. Please reload."
            )}
          </p>
          <button onClick={() => window.location.reload()}>
            {t("actions.reload", "Reload")}
          </button>
        </div>
      </div>
    );
  }

  if (!isInitialized) {
    return (
      <div className={styles.app}>
        <div className={styles.loading}>
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <AuthProvider>
      <ApiProvider>
        <SentryDeviceContext />
        <FontSizeProvider>
          <ClientConfigProvider>
            <SessionShotsProvider>
              <TimeoutConfigProvider>
                <UnitProvider>
                  <div className={styles.app}>
                    <Toaster />
                    <SnackbarToaster />
                    <NotificationToaster />
                    <AppRouter />
                  </div>
                </UnitProvider>
              </TimeoutConfigProvider>
            </SessionShotsProvider>
          </ClientConfigProvider>
        </FontSizeProvider>
      </ApiProvider>
    </AuthProvider>
  );
};
